import { createSlice, Action, SerializedError, current } from '@reduxjs/toolkit'
import { QueryFilterType, StatLevel } from 'store/api/analytics/analytics.interface'
import { ResolverType } from 'components/SetupGuide/SetupGuide.interface'
import { PresetTimeRange } from 'ui/Analytics/PresetTimeRange'
import { clearStateForImpersonation } from 'store/organization'
import { devicesApi } from 'store/api/devices/devices'
import { sessionLogout } from 'store/session'
import { RuleType } from 'store/api/rules'
import { MappedData } from 'components/Dashboard/Analytics/Statistics/Sections/TopDestinations/utils/useGetMappedData'
import { setCurrentProfileId } from 'store/multiprofile/profiles'

interface PayloadAction<T> extends Action {
  payload: T
  error?: SerializedError
}

export interface TimeRange {
  startTs: number
  endTs: number
}

export interface ActivityLogInfo {
  debugUid?: string
  debugLastSessionStartTs?: number
  debugTtl?: number
}

export interface DropdownsData {
  mode?: StatLevel
  deviceId?: string
  region?: string
}

export type ProtocolType = 'all' | ResolverType

// more types can be added if needed
export enum RefinementType {
  ACTIONS = 'actions',
  FILTERS = 'filters',
  SERVICES = 'services',
  DOMAINS = 'domains',
  ENDPOINTS = 'endpoints',
  CLIENTS = 'clients',
  SOURCES = 'sources',
  DESTINATIONS = 'destinations',
  SRC_NETWORKS = 'srcNetworks',
  SRC_ASN = 'srcAsn',
  DST_ASN = 'dstAsn',
  DST_NETWORKS = 'dstNetworks',
}

type RefinementValueType = RuleType | string
type RefinementDataType = { value: RefinementValueType; label?: string }

interface Refinements {
  [RefinementType.ACTIONS]: RefinementDataType[]
  [RefinementType.FILTERS]: RefinementDataType[]
  [RefinementType.SERVICES]: RefinementDataType[]
  [RefinementType.DOMAINS]: RefinementDataType[]
  [RefinementType.ENDPOINTS]: RefinementDataType[]
  [RefinementType.CLIENTS]: RefinementDataType[]
  [RefinementType.SOURCES]: RefinementDataType[]
  [RefinementType.DESTINATIONS]: RefinementDataType[]
  [RefinementType.SRC_NETWORKS]: RefinementDataType[]
  [RefinementType.SRC_ASN]: RefinementDataType[]
  [RefinementType.DST_ASN]: RefinementDataType[]
  [RefinementType.DST_NETWORKS]: RefinementDataType[]
}

export interface RefinementPayload {
  type: RefinementType
  value: RefinementValueType
  label?: string
}

export function doesRefinementValueExist(
  activeRefinementData: RefinementDataType[] = [],
  value: RefinementValueType,
): boolean {
  return activeRefinementData.some(item => item.value === value)
}

function getLabelType(type) {
  switch (type) {
    case RefinementType.SOURCES:
      return 'Src'
    case RefinementType.DESTINATIONS:
      return 'Dest'
    default:
      return ''
  }
}

interface MapPopupData {
  lng: number
  lat: number
  countryCode: string
  countryName: string
}

type PartialData = RefinementPayload & {
  cutOffDate: string
}

export interface AnalyticsState {
  timeRange: TimeRange
  filterType: QueryFilterType
  filterParameter: string
  filterParameters: {
    [key in QueryFilterType]?: string
  }
  // selectedDeviceId is used when filtering by a specific endpoint selected in the endpoints dropdown (activity logs and statistics)
  selectedDeviceId?: string
  selectedClientId?: string
  // deviceIdThatMadeTheLog is used to add/edit a custom rule directly from the activity logs
  deviceIdThatMadeTheLog?: string
  dropdownsData: DropdownsData
  selectedPresetTimeRange: PresetTimeRange
  activeRefinements: Refinements
  mapPopupData?: MapPopupData
  mapData?: MappedData[]
  mapType: RefinementType
  queryLogData?: ActivityLogInfo
  isViewingCustomDateTimePicker?: boolean
  selectedProfileId?: string
  partialData?: PartialData
  forceDataRecalculation?: boolean
  isDataAvailabilityBannerVisibleByUserPk: Record<string, boolean>
}

export const initialEndTs = Date.now()
export const initialAnalyticsTimeRange = {
  startTs: initialEndTs - 1000 * 60 * 60 * 24,
  endTs: initialEndTs,
}

const initialState: AnalyticsState = {
  /* The default time range is last day, though this shouldn't actually matter until it's set by the user
  because we now (#2509) default the user to the PresetTimeRange value of LAST_DAY anyway */
  timeRange: initialAnalyticsTimeRange,
  filterType: QueryFilterType.ALL,
  filterParameter: '',
  filterParameters: {
    [QueryFilterType.FILTERS]: '',
    [QueryFilterType.SERVICES]: '',
    [QueryFilterType.LOCATIONS]: '',
  },
  selectedDeviceId: undefined,
  selectedClientId: undefined,
  deviceIdThatMadeTheLog: undefined,
  dropdownsData: {},
  selectedPresetTimeRange: PresetTimeRange.LAST_DAY,
  mapData: [],
  mapType: RefinementType.SOURCES,
  activeRefinements: {
    [RefinementType.ACTIONS]: [],
    [RefinementType.FILTERS]: [],
    [RefinementType.SERVICES]: [],
    [RefinementType.DOMAINS]: [],
    [RefinementType.ENDPOINTS]: [],
    [RefinementType.CLIENTS]: [],
    [RefinementType.SOURCES]: [],
    [RefinementType.DESTINATIONS]: [],
    [RefinementType.SRC_NETWORKS]: [],
    [RefinementType.SRC_ASN]: [],
    [RefinementType.DST_ASN]: [],
    [RefinementType.DST_NETWORKS]: [],
  },
  isViewingCustomDateTimePicker: false,
  forceDataRecalculation: true,
  isDataAvailabilityBannerVisibleByUserPk: {},
}

export const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setTimeRange(state, { payload }: PayloadAction<TimeRange>): void {
      state.timeRange = payload
    },
    setFilterType(state, { payload }: PayloadAction<QueryFilterType>): void {
      state.filterType = payload
    },
    setFilterParameter(state, { payload }: PayloadAction<string>): void {
      state.filterParameter = payload
    },
    resetFilterParameters(state): void {
      state.filterParameters = initialState.filterParameters
    },

    /**
     * use this to set the selected device id but not fetch all reports
     * use `updateSelectedDeviceId` instead to fetch all reports on device change
     * @param state
     * @param param1
     */
    setSelectedDeviceId(state, { payload }: PayloadAction<string | undefined>): void {
      state.selectedDeviceId = payload
      state.selectedClientId = undefined
    },
    setDeviceIdThatMadeTheLog(state, { payload }: PayloadAction<string>): void {
      state.deviceIdThatMadeTheLog = payload
    },
    setSelectedClientId(state, { payload }: PayloadAction<string | undefined>): void {
      state.selectedClientId = payload
    },
    setSelectedPresetTimeRange(state, { payload }: PayloadAction<PresetTimeRange>): void {
      state.selectedPresetTimeRange = payload
    },
    setMapData(state, { payload }: PayloadAction<MappedData[] | undefined>): void {
      state.mapData = payload
    },
    setMapType(state, { payload }: PayloadAction<RefinementType>): void {
      state.mapType = payload
    },
    setMapPopupData(state, { payload }: PayloadAction<MapPopupData | undefined>): void {
      state.mapPopupData = payload
    },
    setActiveRefinement(state, { payload }: PayloadAction<RefinementPayload>): void {
      const { type, value, label } = payload

      if (!doesRefinementValueExist(state.activeRefinements[type], value)) {
        state.activeRefinements[type]?.push({ value, label })
      }
    },
    removeActiveRefinement(
      state,
      {
        payload,
      }: PayloadAction<{
        type: string
        value?: RefinementValueType
      }>,
    ): void {
      const { type, value } = payload

      if (value === undefined) {
        state.activeRefinements[type] = []
        return
      }

      if (type === RefinementType.ENDPOINTS && !!state.activeRefinements[RefinementType.CLIENTS]) {
        state.activeRefinements[RefinementType.CLIENTS] = []
      }

      state.activeRefinements[type] = state.activeRefinements[type].filter(
        item => item.value !== value,
      )
    },
    resetActiveRefinements(state): void {
      state.activeRefinements = initialState.activeRefinements
    },
    toggleActiveRefinementFromMap(state, { payload }: PayloadAction<string>): void {
      const featureId = payload

      const feature = state.mapData?.find(({ countryCode }) => featureId === countryCode)

      if (!feature) {
        return
      }

      const currentFeature = current(feature)
      const type = state.mapType
      const value = currentFeature?.countryCode ?? ''
      const labelType = getLabelType(type)
      const label = `${labelType ? `${labelType}: ` : ''}${currentFeature?.countryName ?? ''}`

      if (type) {
        if (!doesRefinementValueExist(state.activeRefinements[type], value)) {
          state.activeRefinements[type]?.push({ value, label })
        } else {
          if (value === undefined) {
            state.activeRefinements[type] = []
            return
          }

          state.activeRefinements[type] = state.activeRefinements[type].filter(
            item => item.value !== value,
          )
        }
      }
    },
    setQueryLogData(state, { payload }: PayloadAction<ActivityLogInfo | undefined>): void {
      state.queryLogData = payload
    },
    setIsViewingCustomDateTimePicker(state, { payload }: PayloadAction<boolean>): void {
      state.isViewingCustomDateTimePicker = payload
    },
    setSelectedProfileId(state, { payload }: PayloadAction<string | undefined>): void {
      state.selectedProfileId = payload
    },
    setPartialData(state, { payload }: PayloadAction<PartialData | undefined>): void {
      state.partialData = payload
    },
    setForceDataRecalculation(state, { payload }: PayloadAction<boolean>): void {
      state.forceDataRecalculation = payload
    },
    clearQueryLogData(state): void {
      state.queryLogData = undefined
    },
    setIsDataAvailabilityBannerVisible(
      state,
      { payload }: PayloadAction<{ userPk: string; isVisible: boolean }>,
    ): void {
      state.isDataAvailabilityBannerVisibleByUserPk[payload.userPk] = payload.isVisible
    },
  },
  extraReducers: builder => {
    builder
      .addCase(sessionLogout.fulfilled, state => {
        // Preserve isDataAvailabilityBannerVisibleByUserPk across logout/login cycles
        const isDataAvailabilityBannerVisibleByUserPk =
          state.isDataAvailabilityBannerVisibleByUserPk
        return {
          ...initialState,
          isDataAvailabilityBannerVisibleByUserPk,
        }
      })
      .addCase(clearStateForImpersonation.fulfilled, state => {
        state.selectedDeviceId = undefined
        state.selectedClientId = undefined
      })
      .addMatcher(devicesApi.endpoints.deleteDevice.matchFulfilled, (state, action) => {
        if (state.selectedDeviceId === action.meta.arg.originalArgs.pk) {
          state.selectedDeviceId = undefined
        }
      })
      .addMatcher(devicesApi.endpoints.deleteDeviceClient.matchFulfilled, (state, action) => {
        if (state.selectedClientId === action.meta.arg.originalArgs.clientId) {
          state.selectedClientId = undefined
        }
      })
      .addMatcher(setCurrentProfileId.match, state => {
        // Clear queryLogData when profile changes to match original persist slice behavior
        state.queryLogData = undefined
      })
  },
})

export const {
  setSelectedPresetTimeRange,
  resetFilterParameters,
  setSelectedDeviceId,
  setSelectedClientId,
  setDeviceIdThatMadeTheLog,
  setTimeRange,
  setActiveRefinement,
  removeActiveRefinement,
  setMapPopupData,
  setMapData,
  setMapType,
  toggleActiveRefinementFromMap,
  resetActiveRefinements,
  setQueryLogData,
  clearQueryLogData,
  setIsViewingCustomDateTimePicker,
  setSelectedProfileId,
  setPartialData,
  setForceDataRecalculation,
  setIsDataAvailabilityBannerVisible,
} = analyticsSlice.actions
